import { isEmpty } from 'lodash';
import moment from 'moment';

import { queryAnalytics } from '~/src/dash/api/console-api';
import { ECurrencyType } from '~/src/dash/api/exchange-rates';
import { encodeForm } from '~/src/dash/api/resource';
import { createUrl, restPost } from '~/src/dash/api/rest';
import { Order } from '~/src/dash/components/tables/ListViewTable/common';
import {
  formatDateEndDay,
  formatDateFromUTCToOffset,
  formatDateStartDay,
  formatDateUsingTimezone,
  getStartDate,
  isDateToday,
} from '~/src/dash/utils/date';
import { IActiveFilters } from '~/src/dash/views/dashboards/DashboardHome/common/common';
import { TKey } from '~/src/dash/publicaUI';

export const DASHBOARD_GRAPH_AUCTIONS = 29;
export const DASHBOARD_GRAPH_BUYERS = 60;
export type DashboardGraphID = 29 | 60;

export const getDashboardGraphID = (activeFilters: IActiveFilters): DashboardGraphID => {
  return activeFilters.demand ? DASHBOARD_GRAPH_BUYERS : DASHBOARD_GRAPH_AUCTIONS;
};

export const DASHBOARD_GRAPH_AUCTIONS_MULTIPUB = 104;
export const DASHBOARD_GRAPH_BUYERS_MULTIPUB = 105;
export type DashboardMultiPubGraphID = 104 | 105;

export const getDashboardMultiPubGraphID = (
  activeFilters: IActiveFilters,
): DashboardMultiPubGraphID => {
  return !!activeFilters.demand
    ? DASHBOARD_GRAPH_BUYERS_MULTIPUB
    : DASHBOARD_GRAPH_AUCTIONS_MULTIPUB;
};

export interface IDashboardHomeCardMetrics {
  Revenue: number;
  RevenueTrend: number | null;
  Impressions: number;
  ImpressionsTrend: number | null;
  CPM: number;
  CPMTrend: number | null;
  CPMPerSecond: number;
  CPMPerSecondTrend: number | null;
  RenderRate: number;
  RenderRateTrend: number | null;
  EndpointRequests: number;
  EndpointRequestsTrend: number | null;
  EndpointRequestsECPM: number;
  EndpointRequestsECPMTrend: number | null;
  Auctions: number;
  AuctionsTrend: number | null;
  RenderedPodFillRate: number;
  RenderedPodFillRateTrend: number | null;
  PodFillRate: number;
  PodFillRateTrend: number | null;
  PodClearRate: number;
  PodClearRateTrend: number | null;
  PaidPodClearRate: number;
  PaidPodClearRateTrend: number | null;
  FillRate: number;
  FillRateTrend: number | null;
  BidRequests: number;
  BidRequestsTrend: number | null;
  BidResponses: number;
  BidResponsesTrend: number | null;
  WinningBids: number;
  WinningBidsTrend: number | null;
  BidResponseRate: number;
  BidResponseRateTrend: number | null;
  BidTimeoutRate: number;
  BidTimeoutRateTrend: number | null;
  BidWinRate: number;
  BidWinRateTrend: number | null;
  BidSaverTrueLift: number | null;
  BidSaverImpressions: number | null;
  BidSaverWinningBids: number | null;

  // Smart Floor Metrics
  BidFloorControl: number | null;
  BidFloorOptimized: number | null;
  BidFloorLow: number | null;
  BidFloorHigh: number | null;

  WinningBidsControl: number | null;
  WinningBidsOptimized: number | null;
  WinningBidsLow: number | null;
  WinningBidsHigh: number | null;

  CPMControl: number | null;
  CPMOptimized: number | null;
  CPMLow: number | null;
  CPMHigh: number | null;

  BidResponsesControl: number | null;
  BidResponsesOptimized: number | null;
  BidResponsesLow: number | null;
  BidResponsesHigh: number | null;

  BidRequestsOptimized: number | null;
  BidRequestsControl: number | null;
  BidRequestsLow: number | null;
  BidRequestsHigh: number | null;

  EstimatedRevenueControl: number | null;
  EstimatedRevenueOptimized: number | null;
  EstimatedRevenueLow: number | null;
  EstimatedRevenueHigh: number | null;

  // Smart Floor Metrics - V2
  BidRequestPercentage: number | null;
  EstimatedRevenue: number | null;

  AdjustedFillRate: number;
  AdjustedPodFillRate: number;
  AdjustedPodClearRate: number;
  AdjustedPaidPodClearRate: number;
  AdjustedEndpointRequests: number;
}

export type IDashboardHomeCardMetricKey = keyof IDashboardHomeCardMetrics;

export interface IDashboardChartData {
  Auctions: number;
  BidRequests: number;
  BidResponseRate: number;
  BidResponses: number;
  BidTimeoutRate: number;
  CPM: number;
  CPMPerSecond: number;
  Date: string;
  EndpointRequests: number;
  EndpointRequestsECPM: number;
  FillRate: number;
  Impressions: number;
  PodClearRate: number;
  PaidPodClearRate: number;
  RenderedPodFillRate: number;
  PodFillRate: number;
  RenderRate: number;
  Revenue: number;
  WinningBids: number;
  EndpointLatency: number;
  AuctionLatency: number;
  BidderLatency: number;
  BidErrors: number;
  BidTimeouts: number;
  BidWinRate: number;
  CreationLatency: number;
  WaitLatency: number;
  BidSchedulingIssues: number;

  // Smart Floor Metrics
  BidFloorControl: number;
  BidFloorOptimized: number;
  BidFloorLow: number;
  BidFloorHigh: number;

  WinningBidsControl: number;
  WinningBidsOptimized: number;
  WinningBidsLow: number;
  WinningBidsHigh: number;

  CPMControl: number;
  CPMOptimized: number;
  CPMLow: number;
  CPMHigh: number;

  BidResponsesControl: number;
  BidResponsesOptimized: number;
  BidResponsesLow: number;
  BidResponsesHigh: number;

  BidRequestsOptimized: number;
  BidRequestsControl: number;
  BidRequestsLow: number;
  BidRequestsHigh: number;

  EstimatedRevenueControl: number;
  EstimatedRevenueOptimized: number;
  EstimatedRevenueLow: number;
  EstimatedRevenueHigh: number;

  // Smart Floor Metrics - V2
  BidRequestPercentage: number;
  EstimatedRevenue: number;
}

export type IDashboardChartDataKey = keyof IDashboardChartData;

export interface IDashboardOverviewData {
  MainData: IDashboardChartData[];
  CompareData: IDashboardChartData[];
  CardMetrics: IDashboardHomeCardMetrics;
}

export async function queryHomeDashboardData(
  publisherID: string,
  query: Record<string, any>,
): Promise<IDashboardOverviewData> {
  const reqUrl = `${createUrl('/get_home_dashboard_data', publisherID, 'v1')}`;
  const headers = { 'Content-Type': 'application/x-www-form-urlencoded' };
  const body = encodeForm(query, headers['Content-Type']);
  const response = await restPost<any>(reqUrl, query, { method: 'post', body, headers });
  return response.parsedBody;
}

export async function queryHomeDashboardParentData(
  publisherID: string,
  query: Record<string, any>,
): Promise<IDashboardOverviewData> {
  const reqUrl = `${createUrl('/get_home_dashboard_parent_data', publisherID, 'v1')}`;
  const headers = { 'Content-Type': 'application/x-www-form-urlencoded' };
  const body = encodeForm(query, headers['Content-Type']);
  const response = await restPost<any>(reqUrl, query, { method: 'post', body, headers });
  return response.parsedBody;
}

export interface IDashboardLiveData {
  LiveData: IDashboardChartData[];
  CardMetrics: IDashboardHomeCardMetrics;
}

export async function queryHomeDashboardLiveData(
  publisherID: string,
  query: Record<string, any>,
): Promise<IDashboardLiveData> {
  const reqUrl = `${createUrl('/get_home_dashboard_live_data', publisherID, 'v1')}`;
  const headers = { 'Content-Type': 'application/x-www-form-urlencoded' };
  const body = encodeForm(query, headers['Content-Type']);
  const response = await restPost<any>(reqUrl, query, { method: 'post', body, headers });
  return response.parsedBody;
}

interface IMaxDates {
  maxDateAuctions: moment.Moment | undefined;
  maxDateBuyers: moment.Moment | undefined;
  errorMessage: string;
}

// api call for max auction / buyer dates
export const getMaxAuctionBuyerDates = (
  publisherID: string,
  timezone: string,
  t: (tKey: TKey) => string,
): Promise<IMaxDates> => {
  const startDate = formatDateStartDay(getStartDate(timezone, 0, 'day').startOf('day'));
  const endDate = formatDateEndDay(getStartDate(timezone, 0, 'day').endOf('day'));
  const queries = [
    // max date buyers
    queryAnalytics({
      query: Object.assign(
        Object.assign({
          graph_id: 62,
          start_date: startDate,
          end_date: endDate,
          publisher_id: publisherID,
        }),
      ),
    }),
    // max date auctions
    queryAnalytics({
      query: Object.assign(
        Object.assign({
          graph_id: 63,
          start_date: startDate,
          end_date: endDate,
          publisher_id: publisherID,
        }),
      ),
    }),
  ];
  return Promise.all(queries)
    .then((result) => {
      if (
        !isEmpty(result) &&
        result[0] &&
        result[0][0] &&
        result[0][0].Date &&
        result[1] &&
        result[1][0] &&
        result[1][0].Date
      ) {
        return {
          maxDateAuctions: formatDateFromUTCToOffset(result[0][0].Date, timezone).add(-1, 'hour'),
          maxDateBuyers: formatDateFromUTCToOffset(result[1][0].Date, timezone).add(-1, 'hour'),
          errorMessage: '',
        };
      } else {
        return {
          maxDateAuctions: undefined,
          maxDateBuyers: undefined,
          errorMessage: t('DashboardSnackbarErrorFindingMaxDates'),
        };
      }
    })
    .catch(() => {
      return {
        maxDateAuctions: undefined,
        maxDateBuyers: undefined,
        errorMessage: t('DashboardSnackbarErrorFindingMaxDates'),
      };
    });
};

// get the correct end date for snowflake query
export async function getUpdatedEndDate(
  graphID: 29 | 60 | 104 | 105,
  endDate: moment.Moment,
  publisherID: string,
  timezone: string,
  t: (tKey: TKey) => string,
): Promise<Record<string, string>> {
  let formattedEndDate = formatDateEndDay(endDate.clone());
  let errorMessage = '';
  if (isDateToday(endDate, timezone)) {
    const result = await getMaxAuctionBuyerDates(publisherID, timezone, t);
    const { maxDateAuctions, maxDateBuyers } = result;
    if (
      (graphID === DASHBOARD_GRAPH_AUCTIONS || graphID === DASHBOARD_GRAPH_AUCTIONS_MULTIPUB) &&
      maxDateAuctions
    ) {
      formattedEndDate = formatDateUsingTimezone(maxDateAuctions, timezone);
    }
    if (
      (graphID === DASHBOARD_GRAPH_BUYERS || graphID === DASHBOARD_GRAPH_BUYERS_MULTIPUB) &&
      maxDateBuyers
    ) {
      formattedEndDate = formatDateUsingTimezone(maxDateBuyers, timezone);
    }
    errorMessage = result.errorMessage;
  }
  return { formattedEndDate, errorMessage };
}

export const updateCompareEndDate = (
  compareEndDate: moment.Moment,
  timezone: string,
  startDate?: moment.Moment,
) => {
  const maxHour = Number(moment().tz(timezone).format('k'));
  const newDate = compareEndDate.clone().hour(maxHour).minute(0).second(0);
  if (
    startDate &&
    startDate.format('YYYY-MM-DDTHH:mm:ss') === newDate.format('YYYY-MM-DDTHH:mm:ss')
  ) {
    return newDate.clone().subtract(1, 'days').format('YYYY-MM-DDTHH:mm:ss');
  }
  return newDate.format('YYYY-MM-DDTHH:mm:ss');
};

export const matchEndDateHMS = (endDate: string, compareEndDate: string) => {
  const hour = Number(moment(endDate).format('HH'));
  const minute = Number(moment(endDate).format('mm'));
  const second = Number(moment(endDate).format('ss'));
  return moment(compareEndDate)
    .clone()
    .hour(hour)
    .minute(minute)
    .second(second)
    .format('YYYY-MM-DDTHH:mm:ss');
};

export type LiveItemsKey =
  | 'bidder'
  | 'biddername_device_type'
  | 'biddername_publica_cluster' // key only works for eks-23 control / experiment
  | 'biddername'
  | 'device_type'
  | 'publica_auction_requester'
  | 'publica_biddername'
  | 'publica_cluster'
  | 'publisher_publica_cluster'
  | 'publica_cluster_demand_source'
  | 'publica_cluster_site_id'
  | 'publica_cluster_site_id_demand_source'
  | 'publica_domain'
  | 'publisher_publica_domain'
  | 'publica_deal_id'
  | 'publica_device_type'
  | 'publica_region'
  | 'publisher_id'
  | 'site_bidder'
  | 'site_biddername'
  | 'site_device_type'
  | 'site'
  | 'automated_bidfloor_id_bidfloor_type'; // metrics=3,9,12,73,75 (and control metric #)

const getInitialItemsKey = (activeFilters: IActiveFilters, isLive?: boolean): LiveItemsKey => {
  const { demand, supply, device, deal } = activeFilters;
  if (deal && isLive) return 'publica_deal_id';
  if (!demand && supply && !device) return 'site';
  if (demand && demand === 'Filtered Demand Sources' && !supply && device)
    return 'biddername_device_type';
  if (demand && demand === 'Filtered Demand Sources' && !supply && !device) return 'biddername';
  if (demand && demand === 'Filtered Demand Sources' && supply && !device) return 'site_biddername';
  if (demand && !supply && !device) return 'bidder';
  if (demand && supply && !device) return 'site_bidder';
  if (!demand && !supply && device) return 'device_type';
  if (!demand && supply && device) return 'site_device_type';
  return 'publisher_id';
};

export const getItemsKey = (
  activeFilters: IActiveFilters,
  isLive?: boolean,
  isUsingBidderType?: boolean,
): LiveItemsKey => {
  const itemsKey = getInitialItemsKey(activeFilters, isLive);
  if (isUsingBidderType) {
    switch (itemsKey) {
      case 'biddername':
        return 'bidder';
      case 'site_biddername':
        return 'site_bidder';
    }
  }
  return itemsKey;
};

export interface ITopChannelsInfo {
  SiteID: string;
  SiteName: string;
  Revenue: number;
  Impressions: number;
  CPM: number;
  CPMPerSecond: number;
  EndpointRequests: number;
  RenderRate: number;
  FillRate: number;
  RenderedPodFillRate: number;
  PodFillRate: number;
  PodClearRate: number;
}

export interface ITopChannelsResponse {
  Data: ITopChannelsInfo[];
  TotalRowCount: number;
}

export interface IDashboardTopChannelsParams {
  graph_id:
    | 48 // no demand
    | 61; // with demand
  start_date: string;
  end_date: string;
  timezone: string;
  publisher_id: string;
  site_ids: string;
  bidder_ids: string;
  biddercodes: string;
  devicetypes: string;
  statFilter: string;
  statOrder: Order;
  limit: number;
  offset: number;
  is_live_data_enabled: boolean;
  currency_type: ECurrencyType;
  live_items_key: LiveItemsKey;
}

// populate dashboard top channels table
export async function queryDashboardTopChannels(
  publisherID: string,
  query: IDashboardTopChannelsParams,
): Promise<ITopChannelsResponse> {
  const reqUrl = `${createUrl('/get_dashboard_top_channels', publisherID, 'v1')}`;
  const headers = { 'Content-Type': 'application/x-www-form-urlencoded' };
  const body = encodeForm(query, headers['Content-Type']);
  const response = await restPost<any>(reqUrl, query, { method: 'post', body, headers });
  return response.parsedBody;
}

export interface ITopDemandSourcesInfo {
  DemandSourceID: string;
  DemandSourceName: string;
  BidderType: string;
  Revenue: number;
  Impressions: number;
  CPM: number;
  CPMPerSecond: number;
  EndpointRequests: number;
  RenderRate: number;
  BidResponseRate: number;
  BidTimeoutsRate: number;
}

export interface ITopDemandSourcesResponse {
  Data: ITopDemandSourcesInfo[];
  TotalRowCount: number;
}

export interface IDashboardTopDemandSourcesParams {
  start_date: string;
  end_date: string;
  timezone: string;
  publisher_id: string;
  site_ids: string;
  bidder_ids: string;
  biddercodes: string;
  devicetypes: string;
  statFilter: string;
  statOrder: Order;
  limit: number;
  offset: number;
  is_live_data_enabled: boolean;
  currency_type: ECurrencyType;
  live_items_key: LiveItemsKey;
}

// populate dashboard top demand sources table
export async function queryDashboardTopDemandSources(
  publisherID: string,
  query: IDashboardTopDemandSourcesParams,
): Promise<ITopDemandSourcesResponse> {
  const reqUrl = `${createUrl('/get_dashboard_top_demand_sources', publisherID, 'v1')}`;
  const headers = { 'Content-Type': 'application/x-www-form-urlencoded' };
  const body = encodeForm(query, headers['Content-Type']);
  const response = await restPost<any>(reqUrl, query, { method: 'post', body, headers });
  return response.parsedBody;
}
